import React, { useState, useMemo } from 'react';
import {
  PendingChangesResult,
  ApplicableChange,
  changeTrackingService
} from '../../services/changeTrackingService';

export interface ImportChangesDialogProps {
  pendingChanges: PendingChangesResult;
  onApply: (selectedIds: string[]) => Promise<void>;
  onDiscard: (ids: string[]) => Promise<void>;
  onClose: () => void;
}

export const ImportChangesDialog: React.FC<ImportChangesDialogProps> = ({
  pendingChanges,
  onApply,
  onDiscard,
  onClose
}) => {
  const [selectedChanges, setSelectedChanges] = useState<Set<string>>(
    new Set(pendingChanges.applicable.map(c => c.id))
  );
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group changes by element type
  const groupedChanges = useMemo(() => {
    const groups: Record<string, ApplicableChange[]> = {};
    pendingChanges.applicable.forEach(change => {
      const type = change.elementType;
      if (!groups[type]) groups[type] = [];
      groups[type].push(change);
    });
    return groups;
  }, [pendingChanges.applicable]);

  const handleToggleChange = (changeId: string) => {
    const newSet = new Set(selectedChanges);
    if (newSet.has(changeId)) {
      newSet.delete(changeId);
    } else {
      newSet.add(changeId);
    }
    setSelectedChanges(newSet);
  };

  const handleSelectAll = () => {
    setSelectedChanges(new Set(pendingChanges.applicable.map(c => c.id)));
  };

  const handleSelectNone = () => {
    setSelectedChanges(new Set());
  };

  const handleApply = async () => {
    if (selectedChanges.size === 0) return;

    setIsApplying(true);
    setError(null);

    try {
      await onApply(Array.from(selectedChanges));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Anwenden der Änderungen');
    } finally {
      setIsApplying(false);
    }
  };

  const handleDiscardAll = async () => {
    setIsApplying(true);
    setError(null);

    try {
      const allIds = [
        ...pendingChanges.applicable.map(c => c.id),
        ...pendingChanges.conflicts.map(c => c.id)
      ];
      await onDiscard(allIds);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Verwerfen der Änderungen');
    } finally {
      setIsApplying(false);
    }
  };

  const getChangeTypeIcon = (changeType: string) => {
    switch (changeType) {
      case 'CREATE':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case 'DELETE':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        );
      case 'UPDATE':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-9999">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Manuelle Änderungen gefunden
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Nach dem Import wurden <span className="font-semibold text-green-600 dark:text-green-400">{pendingChanges.applicable.length} anwendbare</span> Änderungen
          {pendingChanges.conflicts.length > 0 && (
            <> und <span className="font-semibold text-yellow-600 dark:text-yellow-400">{pendingChanges.conflicts.length} Konflikte</span></>
          )} gefunden.
        </p>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-200 rounded">
            {error}
          </div>
        )}

        {/* Selection controls */}
        {pendingChanges.applicable.length > 0 && (
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              Alle auswählen
            </button>
            <button
              onClick={handleSelectNone}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Keine auswählen
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {selectedChanges.size} von {pendingChanges.applicable.length} ausgewählt
            </span>
          </div>
        )}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto mb-4 space-y-4">
          {/* Applicable changes by type */}
          {Object.entries(groupedChanges).map(([type, changes]) => (
            <div key={type} className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase mb-3">
                {changeTrackingService.getElementTypeDisplayName(type)} ({changes.length})
              </h3>
              <div className="space-y-2">
                {changes.map(change => (
                  <label
                    key={change.id}
                    className="flex items-center p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedChanges.has(change.id)}
                      onChange={() => handleToggleChange(change.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <div className="ml-3 flex items-center gap-2 flex-1 min-w-0">
                      {getChangeTypeIcon(change.changeType)}
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {change.elementName || change.elementId}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        change.changeType === 'CREATE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        change.changeType === 'UPDATE' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {changeTrackingService.getChangeTypeDisplayName(change.changeType)}
                      </span>
                    </div>
                    {change.description && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-2 hidden sm:block">
                        {change.description}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {/* Conflicts */}
          {pendingChanges.conflicts.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
              <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 uppercase mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Nicht anwendbar ({pendingChanges.conflicts.length})
              </h3>
              <div className="space-y-2">
                {pendingChanges.conflicts.map(conflict => (
                  <div
                    key={conflict.id}
                    className="flex items-center p-3 bg-white/50 dark:bg-gray-800/50 rounded border border-yellow-200 dark:border-yellow-700"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getChangeTypeIcon(conflict.changeType)}
                      <span className="font-medium text-gray-700 dark:text-gray-200 truncate">
                        {conflict.elementName || conflict.elementId}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        conflict.changeType === 'CREATE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        conflict.changeType === 'UPDATE' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {changeTrackingService.getChangeTypeDisplayName(conflict.changeType)}
                      </span>
                    </div>
                    <span className="text-sm text-yellow-700 dark:text-yellow-300 ml-2">
                      {conflict.conflictReason}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleDiscardAll}
            disabled={isApplying}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-50"
          >
            Alle verwerfen
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isApplying}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
            >
              Später entscheiden
            </button>
            <button
              onClick={handleApply}
              disabled={selectedChanges.size === 0 || isApplying}
              className={`px-4 py-2 text-white rounded transition-colors ${
                selectedChanges.size === 0 || isApplying
                  ? 'bg-blue-300 dark:bg-blue-700 cursor-not-allowed'
                  : 'bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-500'
              }`}
            >
              {isApplying ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Wird angewendet...
                </span>
              ) : (
                `${selectedChanges.size} Änderung${selectedChanges.size !== 1 ? 'en' : ''} anwenden`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportChangesDialog;
